import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { env } from '../../config';

/**
 * Reusable Mapbox GL base-map component.
 * Handles map lifecycle (init, resize, cleanup) and exposes the raw
 * mapboxgl.Map instance via ref.getMap() so callers can drive it imperatively.
 */

mapboxgl.accessToken = env.REACT_APP_MAPBOX_TOKEN || '';

const DEFAULT_ZOOM = 17;
const FALLBACK_CENTER = [-104.9903, 39.7392]; // Denver — used only when no coords available
const DEFAULT_WHEEL_ZOOM_RATE = 1 / 1300;
const ZOOM_BUTTON_STEP = 0.1;
const BOUNDS_PAD_KM = 1.2;

// Resolves the maxBounds to apply: pads given {west,east,south,north} by 2km, or
// builds a 2km box around `center` when no bounds were supplied. Returns mapbox
// array form `[[w,s],[e,n]]` or null when no center is available either.
function resolveMaxBounds(bounds, center, maxBoundsPaddingKm) {
  let west, east, south, north;
  if (bounds && bounds.west != null && bounds.east != null) {
    west = bounds.west;
    east = bounds.east;
    south = bounds.south;
    north = bounds.north;
  } else if (center && center[0] != null && center[1] != null) {
    west = east = center[0];
    south = north = center[1];
  } else {
    return null;
  }
  const dLat = maxBoundsPaddingKm / 111;
  const cosLat = Math.max(
    0.01,
    Math.cos((((north + south) / 2) * Math.PI) / 180),
  );
  const dLng = maxBoundsPaddingKm / (111 * cosLat);
  return [
    [west - dLng, south - dLat],
    [east + dLng, north + dLat],
  ];
}

const MapboxView = forwardRef(function MapboxView(
  {
    center,
    zoom = DEFAULT_ZOOM,
    onMapLoad,
    onMoveEnd,
    maxBounds,
    minZoom = 10,
    maxZoom = 22,
    style = 'mapbox://styles/mapbox/streets-v12',
    className = '',
    hideControls = false,
    preserveDrawingBuffer = false,
    wheelZoomRate = DEFAULT_WHEEL_ZOOM_RATE,
    maxBoundsPaddingKm = BOUNDS_PAD_KM,
  },
  ref,
) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current,
  }));

  useEffect(() => {
    if (!containerRef.current) return;

    if (!mapboxgl.accessToken) {
      console.error('MapboxView: REACT_APP_MAPBOX_TOKEN is not set.');
      return;
    }

    const initialCenter =
      center && center[0] != null && center[1] != null
        ? center
        : FALLBACK_CENTER;
    const resolvedBounds = resolveMaxBounds(
      maxBounds,
      center,
      maxBoundsPaddingKm,
    );

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style,
      center: initialCenter,
      zoom,
      minZoom,
      maxZoom,
      attributionControl: false,
      preserveDrawingBuffer,
      bearing: 0,
      pitch: 0,
      dragRotate: false,
      pitchWithRotate: false,
      ...(resolvedBounds ? { maxBounds: resolvedBounds } : {}),
    });

    map.touchZoomRotate.disableRotation();

    if (wheelZoomRate != null && map.scrollZoom?.setWheelZoomRate) {
      map.scrollZoom.setWheelZoomRate(wheelZoomRate);
    }

    // Auto-fit minZoom so the user can't zoom out beyond maxBounds; recompute on resize.
    const applyBoundsMinZoom = () => {
      const b = map.getMaxBounds();
      if (!b) return;
      const cam = map.cameraForBounds(b, { padding: 0 });
      if (cam && Number.isFinite(cam.zoom)) map.setMinZoom(cam.zoom);
    };
    map.once('load', applyBoundsMinZoom);
    map.on('resize', applyBoundsMinZoom);

    // Hard-clamp pan: snap camera center back inside maxBounds on any move.
    map.on('move', () => {
      const b = map.getMaxBounds();
      if (!b) return;
      const c = map.getCenter();
      const w = b.getWest(),
        e = b.getEast(),
        s = b.getSouth(),
        n = b.getNorth();
      const lng = Math.min(e, Math.max(w, c.lng));
      const lat = Math.min(n, Math.max(s, c.lat));
      if (lng !== c.lng || lat !== c.lat) map.setCenter([lng, lat]);
    });

    map.zoomIn = (options) =>
      map.easeTo({ ...options, zoom: map.getZoom() + ZOOM_BUTTON_STEP });
    map.zoomOut = (options) =>
      map.easeTo({ ...options, zoom: map.getZoom() - ZOOM_BUTTON_STEP });

    if (!hideControls) {
      // Compass hidden — rotation is disabled, so the button has no purpose.
      map.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        'top-right',
      );
      map.addControl(
        new mapboxgl.ScaleControl({ maxWidth: 120, unit: 'metric' }),
        'bottom-left',
      );
    }

    map.on('load', () => {
      if (onMapLoad) onMapLoad(map);
    });

    if (onMoveEnd) {
      const handleMoveEnd = () => {
        const c = map.getCenter();
        onMoveEnd({ center: { lat: c.lat, lng: c.lng }, zoom: map.getZoom() });
      };
      map.on('moveend', handleMoveEnd);
      map.on('zoomend', handleMoveEnd);
    }

    mapRef.current = map;

    // Resize Mapbox when its container changes size (e.g. sidebar toggle).
    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) mapRef.current.resize();
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Map is initialised once; all subsequent updates go through the imperative API.

  useEffect(() => {
    if (!mapRef.current) return;
    if (center && center[0] != null && center[1] != null) {
      mapRef.current.setCenter(center);
    }
  }, [center]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const resolved = resolveMaxBounds(maxBounds, center, maxBoundsPaddingKm);
    if (!resolved) return;
    map.setMaxBounds(resolved);
    const cam = map.cameraForBounds(resolved, { padding: 0 });
    if (cam && Number.isFinite(cam.zoom)) map.setMinZoom(cam.zoom);
  }, [maxBounds, center]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      className={`mapbox-view ${className}`}
      style={{ width: '100%', height: '100%' }}
    />
  );
});

export default MapboxView;
